import { useEffect, useState } from 'react';
import { Download, Edit, Eye, FileText, Phone, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ImagePreview } from '@/components/ImagePreview';
import { StatusBadge } from '@/components/StatusBadge';
import { DocumentViewer, resolveDocumentPreviewUrl } from '@/components/DocumentViewer';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { formatDateDisplay, getAgeInYears } from '@/lib/dates';
import { formatAadhaar } from '@/lib/aadhaar';
import { formatFileName, getEducationQualificationLabel, getLifeStatus } from '@/lib/utils';
import type { ViewMemberProps } from '@/types';

interface DocumentToView {
  name: string;
  url?: string;
  title: string;
}

const display = (value?: string | null) => (value?.trim() ? value : '--');

function DetailItem({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-[13px] text-gray-500">{label}</dt>
      <dd className="mt-1 break-words text-sm text-gray-900">{value}</dd>
    </div>
  );
}

const downloadDocument = (name: string, url?: string) => {
  const previewUrl = resolveDocumentPreviewUrl(url);
  if (!previewUrl) return;

  const link = document.createElement('a');
  link.href = previewUrl;
  link.download = name;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.click();
};

export function ViewMember({ member, open, onOpenChange, onEdit }: ViewMemberProps) {
  const [viewingDocument, setViewingDocument] = useState<DocumentToView | null>(null);
  // The drawer host loads the full record (GET /users/{id}/) and passes it in.
  const details = open ? member : null;

  useEffect(() => {
    if (!open) setViewingDocument(null);
  }, [open]);

  const handleEdit = () => {
    if (!details || !onEdit) return;
    // Opening Edit Member replaces this drawer; closing here as well would clear it again.
    onEdit(details);
  };

  const age = getAgeInYears(details?.dob);
  const documents = [
    { title: 'Proof Document 1', name: details?.proof1, url: details?.proof1Url },
    { title: 'Proof Document 2', name: details?.proof2, url: details?.proof2Url },
  ];

  return (
    <>
      <SideDrawer
        open={open}
        onOpenChange={(nextOpen) => {
          // Keep the drawer open while a document preview sits on top of it.
          if (!nextOpen && viewingDocument) return;
          onOpenChange(nextOpen);
        }}
        title="Member Details"
        member={details}
        titleAction={
          onEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEdit}
                  className="h-7 gap-1 px-2 text-xs font-normal text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <Edit className="h-3 w-3" />
                  Edit
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Edit member</TooltipContent>
            </Tooltip>
          )
        }
      >
        {details && (
          <>
            <DrawerSection>
              <div className="flex flex-wrap items-center gap-3.5">
                <ImagePreview src={details.profileImage || details.photo} alt={details.name || 'Member photo'}>
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarImage src={details.profileImage || details.photo} />
                    <AvatarFallback className="bg-[#4369a0]/10 text-sm font-semibold text-[#4369a0]">
                      {details.name.split(' ').map((part) => part[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </ImagePreview>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-gray-900">{details.name}</p>
                  <p className="mt-0.5 text-[13px] text-gray-500">
                    Member since :  {formatDateDisplay(details.joinedDate)}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <StatusBadge status={details.status ?? 'active'} />
                  <StatusBadge status={getLifeStatus(details)} />
                </div>
              </div>
            </DrawerSection>

            <DrawerSection icon={User} title="Personal Details">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                <DetailItem label="Full Name" value={display(details.name)} />
                <DetailItem label="Father/Husband Name" value={display(details.fatherOrHusbandName)} />
                <DetailItem label="Gender" value={display(details.gender)} />
                <DetailItem label="Date of Birth" value={details.dob ? formatDateDisplay(details.dob) : '--'} />
                <DetailItem label="Age" value={age !== null ? `${age} ${age === 1 ? 'year' : 'years'}` : '--'} />
                <DetailItem label="Aadhaar No" value={display(formatAadhaar(details.aadhaarNo))} />
                <DetailItem
                  label="Education Qualification"
                  value={display(getEducationQualificationLabel(details.educationQualification))}
                />
                <DetailItem label="Occupation" value={display(details.occupation)} />
              </dl>
            </DrawerSection>

            <DrawerSection icon={Phone} title="Contact">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                <DetailItem label="Phone Number" value={display(details.phone)} />
                <DetailItem label="Email" value={display(details.email)} />
                <DetailItem label="Address" value={display(details.address)} wide />
              </dl>
            </DrawerSection>

            <DrawerSection icon={FileText} title="Documents">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {documents.map((doc) => {
                  const hasFile = Boolean(doc.name?.trim() || doc.url?.trim());
                  return (
                    <div key={doc.title} className="rounded-lg border border-gray-200/80 bg-gray-50/50 p-4">
                      <p className="text-sm text-gray-700">{doc.title}</p>
                      {hasFile ? (
                        <div className="my-3 flex items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-xs ring-1 ring-gray-200/60">
                            <FileText className="h-5 w-5 text-[#4369a0]" />
                          </div>
                          <p className="text-sm font-medium text-gray-800" title={doc.name}>
                            {formatFileName(doc.name ?? '')}
                          </p>
                        </div>
                      ) : (
                        <p className="my-3 text-sm text-gray-400">No document uploaded</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 border-gray-200 bg-white"
                          disabled={!doc.url?.trim()}
                          onClick={() => setViewingDocument({ name: doc.name ?? '', url: doc.url, title: doc.title })}
                        >
                          <Eye className="mr-1.5 h-3.5 w-3.5" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 border-gray-200 bg-white"
                          disabled={!doc.url?.trim()}
                          onClick={() => downloadDocument(doc.name ?? '', doc.url)}
                        >
                          <Download className="h-3.5 w-3.5" />
                          <span className="sr-only">Download</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DrawerSection>
          </>
        )}
      </SideDrawer>

      <DocumentViewer
        isOpen={Boolean(viewingDocument)}
        onClose={() => setViewingDocument(null)}
        documentName={viewingDocument?.name ?? ''}
        documentUrl={viewingDocument?.url}
        title={viewingDocument?.title ?? 'Document'}
      />
    </>
  );
}
